import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiMessageSquare, FiPhone, FiCheck, FiCheckCircle, FiAlertCircle, FiUpload, FiDownload, FiRefreshCw, FiEdit2, FiToggleLeft, FiToggleRight, FiEye } = FiIcons;

const WhatsAppBot = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'system',
      content: 'WhatsApp Bot connected and monitoring incoming messages...',
      timestamp: '09:00 AM',
    }
  ]);

  const [verifiedSuppliers] = useState([
    {
      id: 'SUP001',
      name: 'Fresh Farms Namibia',
      phone: '+264 81 234 5678',
      email: 'orders@freshfarms.na',
      verified: true
    },
    {
      id: 'SUP002',
      name: 'Windhoek Produce Co',
      phone: '+264 81 987 6543',
      email: 'supply@whkproduce.na',
      verified: true
    },
    {
      id: 'SUP003',
      name: 'Coastal Citrus Ltd',
      phone: '+264 81 456 7890',
      email: 'info@coastalcitrus.na',
      verified: true
    }
  ]);

  const [processing, setProcessing] = useState(false);
  const [stockUpdates, setStockUpdates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Message templates
  const [messageTemplates] = useState([
    {
      id: 'welcome',
      name: 'Welcome Message',
      trigger: 'New supplier registration',
      content: 'Welcome to Fresh Produce Co! Your account has been verified. You can now send price updates via CSV attachment.',
      active: true
    },
    {
      id: 'price_received',
      name: 'Price Update Received',
      trigger: 'CSV file received',
      content: 'Thank you for the price update. We have received your CSV file and are processing the changes. You will be notified once complete.',
      active: true
    },
    {
      id: 'price_confirmed',
      name: 'Price Update Confirmed',
      trigger: 'CSV processed successfully',
      content: 'Price update confirmed! {{item_count}} items have been updated in our system. New prices are now active for incoming stock.',
      active: true
    },
    {
      id: 'error_format',
      name: 'Format Error',
      trigger: 'Invalid CSV format',
      content: 'We could not process your file. Please ensure your CSV follows the required format: SKU, Name, Cost Price, Sell Price. Download template for reference.',
      active: true
    }
  ]);

  // Auto-reply settings
  const [autoReplySettings, setAutoReplySettings] = useState({
    enabled: true,
    businessHoursOnly: false,
    businessHours: { start: '08:00', end: '17:00' },
    autoAcknowledge: true,
    notifyOnError: true
  });

  const toggleAutoReplySetting = (setting) => {
    setAutoReplySettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
    toast.success(`Auto-reply setting updated`);
  };

  const simulateIncomingMessage = (supplierIndex) => {
    const supplier = verifiedSuppliers[supplierIndex];
    setProcessing(true);

    // Step 1: Incoming message
    setTimeout(() => {
      const newMsg = {
        id: messages.length + 1,
        type: 'incoming',
        supplier: supplier,
        content: `Hi, this is ${supplier.name}. Sending today's price update.`,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        verified: true
      };
      setMessages(prev => [...prev, newMsg]);
    }, 500);

    // Step 2: Verification message
    setTimeout(() => {
      const verifyMsg = {
        id: messages.length + 2,
        type: 'system',
        content: `✓ Supplier verified: ${supplier.name} (${supplier.phone})`,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        status: 'verified'
      };
      setMessages(prev => [...prev, verifyMsg]);
    }, 1500);

    // Step 3: CSV file received
    setTimeout(() => {
      const fileMsg = {
        id: messages.length + 3,
        type: 'incoming',
        supplier: supplier,
        content: '📎 price-update.csv',
        isFile: true,
        fileName: 'price-update.csv',
        fileSize: '2.4 KB',
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        csvData: generateMockCSVData(supplier)
      };
      setMessages(prev => [...prev, fileMsg]);
    }, 2500);

    // Step 4: Processing message
    setTimeout(() => {
      const processMsg = {
        id: messages.length + 4,
        type: 'system',
        content: 'Processing CSV file and updating stock prices...',
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        status: 'processing'
      };
      setMessages(prev => [...prev, processMsg]);
    }, 3500);

    // Step 5: Process the CSV and create updates
    setTimeout(() => {
      const csvData = generateMockCSVData(supplier);
      const updates = csvData.map(item => ({
        ...item,
        supplier: supplier.name,
        supplierPhone: supplier.phone,
        timestamp: new Date().toISOString()
      }));

      setStockUpdates(prev => [...updates, ...prev]);

      const successMsg = {
        id: messages.length + 5,
        type: 'system',
        content: `✓ Successfully updated ${csvData.length} items from ${supplier.name}`,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        status: 'success',
        updates: updates
      };
      setMessages(prev => [...prev, successMsg]);

      toast.success(`Stock prices updated from ${supplier.name}`);
      setProcessing(false);
    }, 5000);
  };

  const generateMockCSVData = (supplier) => {
    const products = [
      { sku: 'APL-001', name: 'Granny Smith Apples', oldCost: 12.00, oldSell: 15.50 },
      { sku: 'ORG-023', name: 'Navel Oranges', oldCost: 14.50, oldSell: 18.00 },
      { sku: 'BAN-012', name: 'Cavendish Bananas', oldCost: 9.00, oldSell: 12.00 },
      { sku: 'TOM-045', name: 'Beef Tomatoes', oldCost: 18.00, oldSell: 25.00 }
    ];

    // Random price changes (±5-15%)
    return products.map(product => {
      const costChange = (Math.random() * 0.2 - 0.1); // -10% to +10%
      const newCost = parseFloat((product.oldCost * (1 + costChange)).toFixed(2));
      const marginMultiplier = 1.25; // 25% margin
      const newSell = parseFloat((newCost * marginMultiplier).toFixed(2));

      return {
        sku: product.sku,
        name: product.name,
        oldCost: product.oldCost,
        newCost: newCost,
        oldSell: product.oldSell,
        newSell: newSell,
        change: ((newCost - product.oldCost) / product.oldCost * 100).toFixed(1)
      };
    });
  };

  const downloadCSVTemplate = () => {
    const csv = `SKU,Name,Cost Price,Sell Price
APL-001,Granny Smith Apples,12.00,15.50
ORG-023,Navel Oranges,14.50,18.00
BAN-012,Cavendish Bananas,9.00,12.00
TOM-045,Beef Tomatoes,18.00,25.00`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supplier-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV template downloaded');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9]">
            <span className="text-[#84cc16]">WhatsApp</span> Bot Integration
          </h1>
          <p className="text-[#6b8772] mt-1 font-mono text-sm">Automated supplier communication and price updates</p>
        </div>
        <button
          onClick={downloadCSVTemplate}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiDownload} className="w-4 h-4" />
          <span>Download CSV Template</span>
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Verified Suppliers</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1">{verifiedSuppliers.length}</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Messages Today</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{messages.length}</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-blue-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Price Updates</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">{stockUpdates.length}</p>
            </div>
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
              <SafeIcon icon={FiRefreshCw} className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6b8772] font-mono text-sm">Bot Status</p>
              <p className="text-sm font-bold text-emerald-400 mt-1 flex items-center">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse mr-2"></span>
                Online
              </p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
              <SafeIcon icon={FiPhone} className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Verified Suppliers */}
        <div className="lg:col-span-1">
          <div className="bg-[#111916] rounded-xl border border-[#243129]">
            <div className="p-6 border-b border-[#243129]">
              <h3 className="text-lg font-semibold text-[#e8f5e9]">Verified Suppliers</h3>
              <p className="text-[#6b8772] mt-1 font-mono text-sm">Authorized WhatsApp contacts</p>
            </div>
            <div className="p-6 space-y-4">
              {verifiedSuppliers.map((supplier, index) => (
                <motion.div
                  key={supplier.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-[#e8f5e9]">{supplier.name}</h4>
                        {supplier.verified && (
                          <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-400" />
                        )}
                      </div>
                      <p className="text-sm text-[#6b8772] mt-1">{supplier.id}</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-[#a5c4ad] mb-3">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiPhone} className="w-3 h-3" />
                      <span>{supplier.phone}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => simulateIncomingMessage(index)}
                    disabled={processing}
                    className="w-full bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:bg-[#243129] disabled:text-[#6b8772] disabled:cursor-not-allowed"
                  >
                    {processing ? 'Processing...' : 'Simulate Message'}
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* WhatsApp Chat Interface */}
        <div className="lg:col-span-2">
          <div className="bg-[#111916] rounded-xl border border-[#243129] h-[600px] flex flex-col">
            <div className="p-6 border-b border-[#243129] bg-emerald-900/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#e8f5e9]">WhatsApp Business</h3>
                  <p className="text-sm text-emerald-400/70">Fresh Produce Bot</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#0a0f0d]">
              <AnimatePresence>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`flex ${message.type === 'incoming' ? 'justify-start' : 'justify-center'}`}
                  >
                    {message.type === 'system' ? (
                      <div className="bg-[#1c2822] text-[#a5c4ad] px-4 py-2 rounded-lg text-sm max-w-md text-center">
                        {message.content}
                        {message.updates && (
                          <div className="mt-2 space-y-1 text-left">
                            {message.updates.map((update, idx) => (
                              <div key={idx} className="text-xs bg-[#0a0f0d] border border-[#243129] rounded px-2 py-1 text-[#e8f5e9]">
                                <strong>{update.sku}</strong>: N${update.oldCost} → N${update.newCost}
                                <span className={`ml-2 ${parseFloat(update.change) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                                  ({update.change > 0 ? '+' : ''}{update.change}%)
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="max-w-md">
                        <div className="bg-[#111916] border border-[#243129] rounded-lg p-4">
                          {message.supplier && (
                            <div className="flex items-center space-x-2 mb-2">
                              <div className="w-8 h-8 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-emerald-400">
                                  {message.supplier.name.charAt(0)}
                                </span>
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-[#e8f5e9]">{message.supplier.name}</p>
                                <p className="text-xs text-[#6b8772]">{message.supplier.phone}</p>
                              </div>
                              {message.verified && (
                                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-400" />
                              )}
                            </div>
                          )}

                          {message.isFile ? (
                            <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-3 flex items-center space-x-3">
                              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2">
                                <SafeIcon icon={FiUpload} className="w-5 h-5 text-emerald-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium text-[#e8f5e9]">{message.fileName}</p>
                                <p className="text-xs text-[#6b8772]">{message.fileSize}</p>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[#a5c4ad]">{message.content}</p>
                          )}

                          <p className="text-xs text-[#6b8772] mt-2">{message.timestamp}</p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div className="p-4 border-t border-[#243129] bg-[#0a0f0d]">
              <p className="text-sm text-[#6b8772] text-center">
                Click "Simulate Message" on any supplier to see the automated process
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Updates Table */}
      {stockUpdates.length > 0 && (
        <div className="bg-[#111916] rounded-xl border border-[#243129]">
          <div className="p-6 border-b border-[#243129]">
            <h3 className="text-lg font-semibold text-[#e8f5e9]">Recent Price Updates</h3>
            <p className="text-[#6b8772] mt-1 font-mono text-sm">Stock items updated via WhatsApp</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0f0d] border-b border-[#243129]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6b8772] uppercase tracking-wider">SKU / Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6b8772] uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6b8772] uppercase tracking-wider">Old Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6b8772] uppercase tracking-wider">New Cost</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6b8772] uppercase tracking-wider">Change</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#6b8772] uppercase tracking-wider">New Sell Price</th>
                </tr>
              </thead>
              <tbody className="bg-[#111916] divide-y divide-[#243129]">
                {stockUpdates.slice(0, 10).map((update, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-[#1c2822] transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-[#e8f5e9]">{update.name}</div>
                        <div className="text-sm text-[#6b8772]">{update.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#a5c4ad]">{update.supplier}</div>
                      <div className="text-xs text-[#6b8772]">{update.supplierPhone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6b8772]">
                      N$ {update.oldCost.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#e8f5e9]">
                      N$ {update.newCost.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        parseFloat(update.change) > 0
                          ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                          : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      }`}>
                        {update.change > 0 ? '+' : ''}{update.change}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-emerald-400">
                      N$ {update.newSell.toFixed(2)}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-sky-400 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-sky-300">How It Works</h4>
            <ul className="text-sm text-sky-400/80 mt-2 space-y-1 list-disc list-inside">
              <li>Suppliers send price updates via WhatsApp with CSV attachments</li>
              <li>System verifies sender using registered phone number</li>
              <li>CSV is automatically parsed and validated</li>
              <li>Stock prices are updated in real-time</li>
              <li>Old stock remains at previous pricing, new stock uses updated prices</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Message Templates & Auto-Reply Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Templates Section */}
        <div className="bg-[#111916] rounded-xl border border-[#243129]">
          <div className="p-6 border-b border-[#243129]">
            <h3 className="text-lg font-semibold text-[#e8f5e9]">Message Templates</h3>
            <p className="text-[#6b8772] mt-1 font-mono text-sm">Automated response templates</p>
          </div>
          <div className="p-6 space-y-4">
            {messageTemplates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-[#e8f5e9]">{template.name}</h4>
                    <p className="text-xs text-[#6b8772] mt-0.5">Trigger: {template.trigger}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPreviewTemplate(template)}
                      className="p-1.5 bg-sky-500/10 border border-sky-500/20 rounded hover:bg-sky-500/20 transition-colors"
                      title="Preview template"
                    >
                      <SafeIcon icon={FiEye} className="w-4 h-4 text-sky-400" />
                    </button>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${template.active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-gray-500/10 text-gray-400 border border-gray-500/20'}`}>
                      {template.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-[#a5c4ad] line-clamp-2">{template.content}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Auto-Reply Settings Section */}
        <div className="bg-[#111916] rounded-xl border border-[#243129]">
          <div className="p-6 border-b border-[#243129]">
            <h3 className="text-lg font-semibold text-[#e8f5e9]">Auto-Reply Settings</h3>
            <p className="text-[#6b8772] mt-1 font-mono text-sm">Configure automated responses</p>
          </div>
          <div className="p-6 space-y-4">
            {/* Enable Auto-Reply */}
            <div className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg">
              <div>
                <h4 className="font-medium text-[#e8f5e9]">Enable Auto-Reply</h4>
                <p className="text-xs text-[#6b8772] mt-0.5">Automatically respond to incoming messages</p>
              </div>
              <button
                onClick={() => toggleAutoReplySetting('enabled')}
                className={`p-1 rounded-full transition-colors ${autoReplySettings.enabled ? 'bg-emerald-500/20' : 'bg-gray-500/20'}`}
              >
                <SafeIcon
                  icon={autoReplySettings.enabled ? FiToggleRight : FiToggleLeft}
                  className={`w-8 h-8 ${autoReplySettings.enabled ? 'text-emerald-400' : 'text-gray-400'}`}
                />
              </button>
            </div>

            {/* Business Hours Only */}
            <div className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg">
              <div>
                <h4 className="font-medium text-[#e8f5e9]">Business Hours Only</h4>
                <p className="text-xs text-[#6b8772] mt-0.5">Only send auto-replies during business hours (08:00 - 17:00)</p>
              </div>
              <button
                onClick={() => toggleAutoReplySetting('businessHoursOnly')}
                className={`p-1 rounded-full transition-colors ${autoReplySettings.businessHoursOnly ? 'bg-emerald-500/20' : 'bg-gray-500/20'}`}
              >
                <SafeIcon
                  icon={autoReplySettings.businessHoursOnly ? FiToggleRight : FiToggleLeft}
                  className={`w-8 h-8 ${autoReplySettings.businessHoursOnly ? 'text-emerald-400' : 'text-gray-400'}`}
                />
              </button>
            </div>

            {/* Auto-Acknowledge */}
            <div className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg">
              <div>
                <h4 className="font-medium text-[#e8f5e9]">Auto-Acknowledge</h4>
                <p className="text-xs text-[#6b8772] mt-0.5">Send read receipts for incoming messages</p>
              </div>
              <button
                onClick={() => toggleAutoReplySetting('autoAcknowledge')}
                className={`p-1 rounded-full transition-colors ${autoReplySettings.autoAcknowledge ? 'bg-emerald-500/20' : 'bg-gray-500/20'}`}
              >
                <SafeIcon
                  icon={autoReplySettings.autoAcknowledge ? FiToggleRight : FiToggleLeft}
                  className={`w-8 h-8 ${autoReplySettings.autoAcknowledge ? 'text-emerald-400' : 'text-gray-400'}`}
                />
              </button>
            </div>

            {/* Notify on Error */}
            <div className="flex items-center justify-between p-4 bg-[#0a0f0d] border border-[#1c2822] rounded-lg">
              <div>
                <h4 className="font-medium text-[#e8f5e9]">Notify on Error</h4>
                <p className="text-xs text-[#6b8772] mt-0.5">Alert admin when message processing fails</p>
              </div>
              <button
                onClick={() => toggleAutoReplySetting('notifyOnError')}
                className={`p-1 rounded-full transition-colors ${autoReplySettings.notifyOnError ? 'bg-emerald-500/20' : 'bg-gray-500/20'}`}
              >
                <SafeIcon
                  icon={autoReplySettings.notifyOnError ? FiToggleRight : FiToggleLeft}
                  className={`w-8 h-8 ${autoReplySettings.notifyOnError ? 'text-emerald-400' : 'text-gray-400'}`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Template Preview Modal */}
      <AnimatePresence>
        {previewTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setPreviewTemplate(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-[#243129]">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold text-[#e8f5e9]">Message Preview</h3>
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="p-2 hover:bg-[#243129] rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiIcons.FiX} className="w-5 h-5 text-[#6b8772]" />
                  </button>
                </div>
              </div>
              <div className="p-5">
                <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 mb-4">
                  <p className="text-xs text-[#6b8772] uppercase font-mono mb-1">Template Name</p>
                  <p className="text-[#e8f5e9] font-medium">{previewTemplate.name}</p>
                </div>
                <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 mb-4">
                  <p className="text-xs text-[#6b8772] uppercase font-mono mb-1">Trigger Event</p>
                  <p className="text-[#a5c4ad]">{previewTemplate.trigger}</p>
                </div>
                <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-4">
                  <p className="text-xs text-emerald-400 uppercase font-mono mb-2">WhatsApp Message Preview</p>
                  <div className="bg-[#075e54] rounded-lg p-3 shadow-lg">
                    <p className="text-white text-sm">{previewTemplate.content}</p>
                    <p className="text-xs text-white/60 text-right mt-2">✓✓ 10:30 AM</p>
                  </div>
                </div>
              </div>
              <div className="p-5 border-t border-[#243129] flex justify-end">
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="px-4 py-2 bg-[#243129] text-[#e8f5e9] rounded-lg hover:bg-[#2d3f35] transition-colors"
                >
                  Close Preview
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WhatsAppBot;
